/**
 * Shared voice-leading primitives — the leaf module that both the analyzer
 * (`voice-leading.ts`) and the Bowie-locked voicing tables
 * (`voice-leading-tables.ts`) depend on. Extracted here to break the import
 * cycle between those two modules: tables need `splitNote` + the voicing types,
 * while the analyzer needs `getVoicingPair` from tables.
 */

import type { NoteName } from '@tunasong/schemas'

export const VOICE_LEADING_KEY_POOL = [
  'C', 'G', 'D', 'A', 'E', 'B', 'F#', 'Db', 'Ab', 'Eb', 'Bb', 'F',
] as const
export type VoiceLeadingKey = (typeof VOICE_LEADING_KEY_POOL)[number]

export const CHORD_PAIRS = ['I-V', 'I-IV', 'V-I', 'IV-V'] as const
export type ChordPair = (typeof CHORD_PAIRS)[number]

export interface VoicedChord {
  /** Pitches low → high, e.g. ['C3', 'E3', 'G3']. Implicit inversion. */
  pitches: readonly NoteName[]
}

export interface VoicingPair {
  smooth: readonly VoicedChord[]
  awkward: readonly VoicedChord[]
  pair: ChordPair
  key: VoiceLeadingKey
}

export const splitNote = (note: NoteName): { pitch: string; octave: number } => {
  const match = /^([A-G][#b]?)(-?\d+)$/.exec(note)
  if (!match) {throw new Error(`Invalid note: ${note}`)}
  return { pitch: match[1], octave: Number(match[2]) }
}
